import { selectedFragmentElementAtom, selectedLayersAtom } from '@/atoms';
import type { FragmentPropertyNode } from '@/atoms/fragmentSelection.atom';
import { getLinkSessionId } from '@/graphql/execute';
import { useFeature } from '@/hooks/useFeature';
import { useOrganizations } from '@/hooks/useOrganizations';
import { ANALYSIS_DIALOG_OPTIONS, ANALYSIS_SHELL_CLASS } from '@/lib/analysisDialog';
import { useDialog } from '@/providers/DialogProvider';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown, ChevronRight, Crosshair, Download, Loader2, MoreHorizontal } from 'lucide-react';
import { useState, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ID } from './ID';
import { jumpToFragment } from './IFC2';
import LandxmlExportDialog from './LandxmlExportDialog';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Label } from './ui/label';

/**
 * Collapsible property group (relation / related item).
 */
const PropertyGroup = ({ node }: { node: FragmentPropertyNode }) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation('components');

  const label = node.type === 'relation' ? node.name : `${t('fragmentProperties.element')} #${node.name}`;

  return (
    <div className='flex flex-col gap-1'>
      <button
        type='button'
        className='flex items-center gap-1 text-sm font-medium text-left hover:opacity-80'
        onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown className='size-3.5' /> : <ChevronRight className='size-3.5' />}
        {label}
      </button>
      {open && node.children && (
        <div className='ml-3 flex flex-col gap-1 border-l border-slate-200/70 dark:border-stone-700/70 pl-3'>
          {node.children.map((child, i) => (
            <PropertyNodeView key={`${child.name}-${child.localId}-${i}`} node={child} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Renders a single property node – either an attribute (leaf) or a group.
 */
const PropertyNodeView = ({ node }: { node: FragmentPropertyNode }) => {
  if (node.type === 'relation' || node.type === 'related') {
    return <PropertyGroup node={node} />;
  }

  // Attribute leaf
  return (
    <div className='flex flex-col gap-0.5'>
      <span className='text-xs text-muted-foreground'>{node.name}</span>
      <span className='text-sm break-all'>{String(node.value ?? '')}</span>
    </div>
  );
};

const FragmentProperties: FunctionComponent = () => {
  const { projectId } = useParams();
  const [selected, setSelected] = useAtom(selectedFragmentElementAtom);
  const layers = useAtomValue(selectedLayersAtom(projectId!));
  const layer = layers.find((l) => l.id === selected?.layerId);
  const { t } = useTranslation('components');
  const dialog = useDialog();
  const { hasRole } = useOrganizations();
  // Rarely-needed actions live behind a "…" menu. The menu is not rendered at
  // all unless the user may actually run them: subscription gate (hide mode,
  // per CLAUDE.md), EDITOR role, and not a public link session.
  const landxmlGate = useFeature('export.landxml');
  const canExportLandxml = landxmlGate.allowed && hasRole('EDITOR') && !getLinkSessionId();

  const handleClose = () => setSelected(null);

  // IFC2 prepends the element's IfcGloballyUniqueId as a synthetic 'GUID'
  // attribute row; surface it as a click-to-copy field like the ids above and
  // keep it out of the generic property list so it isn't shown twice.
  const guidNode = selected?.properties?.find((n) => n.name === 'GUID' && n.type !== 'relation' && n.type !== 'related');
  const guid = typeof guidNode?.value === 'string' ? guidNode.value : undefined;
  const propertyNodes = (selected?.properties ?? []).filter((n) => n !== guidNode);

  const handleExportLandxml = () => {
    if (!selected) return;
    dialog.openDialog(
      <LandxmlExportDialog
        projectId={projectId!}
        layerId={selected.layerId}
        layerName={layer?.name ?? selected.modelId}
        initialLocalIds={[selected.localId]}
      />,
      ANALYSIS_SHELL_CLASS,
      ANALYSIS_DIALOG_OPTIONS,
    );
  };

  return (
    <Drawer open={!!selected} onClose={handleClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>{t('fragmentProperties.title')}</DrawerTitle>
        </DrawerHeader>
        {/* Anchored to the drawer's top-right corner (DrawerContent is `fixed`),
            independent of the header's fixed width. */}
        {canExportLandxml && selected && (
          <div className='absolute right-3 top-3'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='icon' className='size-8' aria-label={t('fragmentProperties.moreActions')}>
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem onClick={handleExportLandxml}>
                  <Download className='size-4' />
                  {t('fragmentProperties.exportLandxml')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        <div className='flex-1 overflow-y-auto'>
          <div className='flex flex-col gap-4 px-4 py-2'>
            {/* Layer name */}
            <div className='flex flex-col gap-1'>
              <Label>{t('fragmentProperties.layer')}</Label>
              <ID value={layer?.name || selected?.modelId || ''} />
            </div>

            {/* Local ID */}
            <div className='flex flex-col gap-1'>
              <Label>{t('fragmentProperties.localId')}</Label>
              <ID value={String(selected?.localId ?? '')} />
            </div>

            {/* GUID (IfcGloballyUniqueId) */}
            {guid && (
              <div className='flex flex-col gap-1'>
                <Label>{t('fragmentProperties.guid')}</Label>
                <ID value={guid} />
              </div>
            )}

            {selected && (
              <Button
                variant='outline'
                size='sm'
                className='self-start'
                onClick={() => jumpToFragment(selected.modelId, selected.localId)}>
                <Crosshair className='size-4' />
                {t('fragmentProperties.jumpTo')}
              </Button>
            )}

            {/* Properties tree */}
            {selected?.loading ? (
              <div className='flex items-center gap-2 text-muted-foreground text-sm'>
                <Loader2 className='size-4 animate-spin' />
                {t('fragmentProperties.loadingProperties')}
              </div>
            ) : propertyNodes.length > 0 ? (
              <div className='flex flex-col gap-3'>
                <Label>{t('fragmentProperties.properties')}</Label>
                {propertyNodes.map((node, i) => (
                  <PropertyNodeView key={`${node.name}-${node.localId}-${i}`} node={node} />
                ))}
              </div>
            ) : (
              <p className='text-muted-foreground text-sm'>{t('fragmentProperties.noProperties')}</p>
            )}
          </div>
        </div>
        <DrawerFooter>
          <Button variant='link' onClick={handleClose}>
            {t('fragmentProperties.close')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FragmentProperties;
